import { Navigate, Outlet, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { maySeeWithSystemOrAnyRole } from './claimFilterAudience';
import { isRepairerUser } from './roles';
import { isFitterPath, loginPathFor } from './loginPath';

/**
 * S021.5c §3.2 — "geen verbinding": de bestaande-sessie is verlopen en het hervatten met het
 * toestel-token lukte nu niet (503 of netwerk). Opnieuw proberen haalt whoami opnieuw op; die
 * 401 start (na de wachttijd) een nieuwe hervat-poging.
 */
function ResumeUnavailablePanel() {
  const queryClient = useQueryClient();
  return (
    <div
      role="alert"
      data-testid="resume-unavailable"
      className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 px-5 text-center"
    >
      <p className="text-[15px] font-semibold text-slate-800">Geen verbinding met CARculate</p>
      <p className="text-[13px] text-slate-600">Je blijft ingelogd. Probeer het zo opnieuw.</p>
      <button
        type="button"
        onClick={() => void queryClient.refetchQueries({ queryKey: ['auth', 'whoami'], exact: true })}
        className="min-h-[48px] rounded-xl bg-[#132679] px-5 text-[15px] font-bold text-white"
      >
        Opnieuw proberen
      </button>
    </div>
  );
}

/**
 * Route-gate. Drie onafhankelijke, optionele eisen:
 *  - `roles`: minstens ÉÉN van deze rol-claims (`.some()`), spiegelt de
 *    `Tab.requiresAnyRole`-zichtbaarheid.
 *  - `systemOnly`: alleen system-gebruikers (`userContextType === 'System'`),
 *    spiegelt de `App.requiresSystemContext`-zichtbaarheid in AppSwitcher.
 *  - `repairersOnly`: alleen hersteller-users (`userContextType ∈ {RepairerHQ,
 *    RepairerLocation}`), spiegelt de `Tab.requiresRepairerContext`-zichtbaarheid
 *    (grootboeken-hersteller-only) én de BE-gate `LedgersController.IsRepairerContext`.
 *
 * Alle weringen redirecten naar `/` (mirror van de bestaande role-gate), zodat
 * een niet-gerechtigde user nooit op het scherm landt.
 */
export function ProtectedRoute({
  roles,
  systemOnly,
  repairersOnly,
  systemOrAnyRole,
  canManageUsers,
  canManageDealerSurcharge,
}: {
  roles?: string[];
  systemOnly?: boolean;
  repairersOnly?: boolean;
  /**
   * Story `fe-be-claimfilter-beheerscherm` (2026-08-31, AC-5.3) — de **OF**-poort:
   * System-context **ÓF** minstens één van deze rollen.
   *
   * ⚠⚠ De drie eisen hierboven componeren met **EN** (opeenvolgende vroege
   * returns). Voor deze routes worden ze daarom BEWUST niet gebruikt:
   * `systemOnly` samen met `roles={['helpdesk']}` zou de route voor NIEMAND
   * bereikbaar maken — zonder foutmelding, en dus van buiten niet te
   * onderscheiden van "nog niet gebouwd".
   *
   * Hij leest `maySeeWithSystemOrAnyRole`: dezelfde functie die `AppSwitcher` en
   * `AppTabs` gebruiken en die het declaratieve
   * `App.requiresSystemContextOrAnyRole` voedt. Eén definitie voor tegel, tabs én
   * route — en dezelfde twee termen als de BE-poort (AC-40).
   */
  systemOrAnyRole?: readonly string[];
  /**
   * Story `be-fe-systeem-gebruikers` (AC 4) — Systeem → Gebruikers: alleen bij whoami `canManageUsers === true` (System
   * of het keten-hoofdkantoor). Bewust NIET `systemOnly`: het keten-HK moet erbij. De autoriteit blijft het endpoint.
   */
  canManageUsers?: boolean;
  /**
   * Story `be-fe-meerprijs-dealer-kortingen` (AC 11.1) — Relatiebeheer → Meerprijs dealer: alleen bij whoami
   * `canManageDealerSurcharge === true` (vlag aan én System of keten-hoofdkantoor). Bewust NIET `systemOnly`: het
   * keten-HK hoort erbij. De autoriteit blijft het endpoint.
   */
  canManageDealerSurcharge?: boolean;
}) {
  const { user, isLoading, isAuthenticated, resumeUnavailable, whoamiOffline } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Laden…
      </div>
    );
  }

  // S021.5c §3.2 / AC 16: de sessie is weg en hervatten kan NU niet (503, geen netwerk). Dan
  // NIET naar het inlogscherm — de monteur logt anders onnodig met wachtwoord in terwijl zijn
  // toestel-token nog geldig is. Een foutstaat met opnieuw proberen.
  if ((!isAuthenticated || !user) && resumeUnavailable && isFitterPath(location.pathname)) {
    return <ResumeUnavailablePanel />;
  }

  // S021.5d §A.4 (G-1): op /monteur… zonder verbinding NIET naar het inlogscherm (offline kan
  // niemand inloggen). De monteur-schil beslist zelf (`coldStartDecision`): de bewaarde gegevens
  // van vandaag, of "Geen verbinding". Alleen het monteurpad; het kantoor verandert niet.
  if ((!isAuthenticated || !user) && whoamiOffline && isFitterPath(location.pathname)) {
    return <Outlet />;
  }

  if (!isAuthenticated || !user) {
    // S021.5b §3.2: binnen de monteur-app naar zijn eigen inlogscherm (/monteur/inloggen).
    return <Navigate to={loginPathFor(location.pathname)} replace state={{ from: location }} />;
  }

  if (roles && !roles.some((r) => user.roles.includes(r))) {
    return <Navigate to="/" replace />;
  }

  // System-context-gate: niet-system-users worden geweerd (zelfde redirect als
  // de role-gate). Houdt /system/* exclusief voor `userContextType === 'System'`.
  if (systemOnly && user.userContextType !== 'System') {
    return <Navigate to="/" replace />;
  }

  // Hersteller-context-gate: alleen RepairerHQ/RepairerLocation (zelfde redirect
  // als de andere gates). Houdt hersteller-only routes (bv. Grootboeken) weg van
  // System/RegularUser — de BE weigert die ook met 403 no_repairer_context.
  if (repairersOnly && !isRepairerUser(user)) {
    return <Navigate to="/" replace />;
  }

  // OF-poort (claimfilter-beheerscherm): System-context ÓF de support-rol. Zie de
  // prop-doc voor waarom dit NIET met `systemOnly` + `roles` gebouwd is.
  if (systemOrAnyRole && !maySeeWithSystemOrAnyRole(user, systemOrAnyRole)) {
    return <Navigate to="/" replace />;
  }

  // Systeem → Gebruikers (be-fe-systeem-gebruikers): fail-closed op `=== true`.
  if (canManageUsers && user.canManageUsers !== true) {
    return <Navigate to="/" replace />;
  }

  // Meerprijs dealer (be-fe-meerprijs-dealer-kortingen): fail-closed op `=== true`.
  if (canManageDealerSurcharge && user.canManageDealerSurcharge !== true) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
